import React, { useState, useEffect } from "react";
import moment from "moment-timezone";
import DatePicker from "react-datepicker";
import InputIcon from "react-multi-date-picker/components/input_icon";
import ReactTooltip from "react-tooltip";
import DISPLAY_TEXTS from "../../helpers/displayTexts";
import { getDayFirst } from "../../helpers/helpers";
import { BsCheckCircleFill } from "react-icons/bs";

const PublicRegisterInviteFormComponent = ({
  title,
  description,
  recipients,
  expiry,
  multipleUse,
  errorState,
  updateInputChange,
}) => {
  const [currentEmail, setCurrentEmail] = useState("");
  const [addedEmails, setAddedEmails] = useState([]);
  const [emailValidationError, setEmailValidationError] = useState("");
  const [currentEmailError, setCurrentEmailError] = useState(false);

  useEffect(() => {
    if (recipients && recipients.trim()) {
      const emailArray = recipients
        .split(",")
        .map((email) => email.trim())
        .filter((email) => email);
      setAddedEmails(emailArray);
    } else {
      setAddedEmails([]);
    }
  }, [recipients]);

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validateAllEmails = (emails) => {
    // If there's text in the input field, validate it first
    if (currentEmail && currentEmail.trim()) {
      if (!isValidEmail(currentEmail.trim())) {
        return "Please enter valid email address";
      }
    }

    // If we have valid emails in the list, it's valid
    if (emails.length > 0) {
      return "";
    }

    // If no emails in list and no current input, show required error only if there was interaction
    if ((!currentEmail || !currentEmail.trim()) && emails.length === 0) {
      if (errorState?.recipients) {
        return "Recipients field is required";
      }
      return "";
    }

    return "";
  };

  useEffect(() => {
    if (updateInputChange) {
      const validationError = validateAllEmails(addedEmails);
      updateInputChange({
        target: {
          name: "recipients",
          value: addedEmails.join(", "),
          validationError: validationError,
          currentEmail: currentEmail,
        },
      });
    }
  }, [currentEmail, addedEmails]);

  const handleCurrentEmailChange = (value) => {
    setCurrentEmail(value);

    if (value && value.trim()) {
      const isValid = isValidEmail(value.trim());
      setCurrentEmailError(!isValid);
      if (!isValid) {
        setEmailValidationError("Please enter valid email address");
      } else {
        setEmailValidationError("");
      }
    } else {
      setCurrentEmailError(false);
      setEmailValidationError("");
    }
  };

  const addEmail = () => {
    const emailToAdd = currentEmail.trim();

    if (!emailToAdd) {
      setEmailValidationError("Please enter an email address");
      return;
    }

    if (!isValidEmail(emailToAdd)) {
      setCurrentEmailError(true);
      setEmailValidationError("Please enter a valid email address");
      return;
    }

    // Check if email already exists
    if (addedEmails.includes(emailToAdd)) {
      setEmailValidationError("Email already added");
      return;
    }

    const newAddedEmails = [...addedEmails, emailToAdd];
    setAddedEmails(newAddedEmails);
    setCurrentEmail("");
    setCurrentEmailError(false);
    setEmailValidationError("");

    const emailString = newAddedEmails.join(", ");
    updateInputChange({
      target: {
        name: "recipients",
        value: emailString,
        validationError: "",
      },
    });
  };

  const removeEmail = (emailToRemove) => {
    const newAddedEmails = addedEmails.filter(
      (email) => email !== emailToRemove
    );
    setAddedEmails(newAddedEmails);

    const validationError = validateAllEmails(newAddedEmails);
    setEmailValidationError(validationError);

    const emailString = newAddedEmails.join(", ");

    updateInputChange({
      target: {
        name: "recipients",
        value: emailString,
        validationError: validationError,
      },
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addEmail();
    }
  };

  const canAddEmail = () => {
    return currentEmail && currentEmail.trim() && isValidEmail(currentEmail);
  };

  const handleBlur = (e) => {
    if (canAddEmail()) {
      addEmail();
    }
  };

  const displayEmailError =
    emailValidationError || errorState?.recipients || "";

  return (
    <>
      <div
        className={`form-group invite-add-form position-relative error-l-75 mb-5 ${
          errorState?.description ? "hasValidationError" : ""
        }`}
      >
        <label>
          <b>Description *</b>
        </label>
        <input
          type="text"
          required
          value={description}
          name="description"
          className="form-control"
          placeholder="Enter invite description"
          onChange={(e) => updateInputChange(e)}
        />
        <span className="validation-error-message">
          {errorState && errorState.description ? errorState?.description : ""}
        </span>
      </div>

      <div
        className={`form-group position-relative error-l-75 mb-5 ${
          currentEmailError ? "hasValidationError" : ""
        } `}
      >
        <label>
          <b>Email(s) *</b>
        </label>

        <div className="email-input-row d-flex align-items-start">
          <div className="email-input-container invite-add-form d-flex align-items-stretch flex-grow-1">
            <input
              type="email"
              className={`form-control ${
                currentEmailError ? "is-invalid" : ""
              }`}
              value={currentEmail}
              placeholder="Enter email address"
              onChange={(e) => handleCurrentEmailChange(e.target.value)}
              onKeyPress={handleKeyPress}
              onBlur={handleBlur}
              style={{ borderRight: "none" }}
            />
            <button
              type="button"
              className="btn btn-secondary rounded-0 rounded-end d-flex align-items-center justify-content-center px-3"
              onClick={addEmail}
              disabled={!canAddEmail()}
              title={canAddEmail() ? "Add email" : "Enter a valid email first"}
              style={{
                fontSize: "1.2rem",
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
              }}
            >
              <span style={{ lineHeight: "1" }}>+</span>
            </button>
          </div>
        </div>

        {addedEmails.length > 0 && (
          <>
            <div className="mb-2 mt-2">
              <small>Added emails:</small>
            </div>
            <div className="added-emails-list">
              {addedEmails.map((email, index) => (
                <div
                  key={index}
                  className="added-email-item d-flex align-items-center justify-content-between mb-2 px-2 py-2 self-registration-emails"
                >
                  <span className="email-text d-flex align-items-center">
                    <BsCheckCircleFill
                      className="mr-2 email-icon text-success"
                      style={{ fontSize: "1rem", verticalAlign: "middle" }}
                    />
                    <span>{email}</span>
                  </span>
                  <span
                    className="text-danger cursor-pointer"
                    style={{ fontSize: "1.8rem", lineHeight: "1" }}
                    onClick={() => removeEmail(email)}
                  >
                    ×
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {displayEmailError && (
          <span className="validation-error-message">{displayEmailError}</span>
        )}
      </div>

      <div
        className={`form-group position-relative error-l-75 mb-5 ${
          errorState?.expiry ? "hasValidationError" : ""
        } ${displayEmailError ? "mt-5" : ""}`}
      >
        <label>
          <b>Expiry</b>{" "}
          <i
            className="infoButton"
            data-tip={DISPLAY_TEXTS.INVITEFORM_EXPIRY_TOOLTIP}
          >
            i
          </i>
        </label>
        <div className="input-group date invite-add-form">
          <DatePicker
            className={`form-control`}
            render={<InputIcon className="form-control" />}
            dateFormat="dd/MM/yyyy"
            placeholderText="DD/MM/YYYY"
            minDate={moment().toDate()}
            popperPlacement="bottom-end"
            selected={getDayFirst(expiry)}
            onChange={(date) =>
              updateInputChange({
                target: {
                  name: "expiry",
                  value: date ? date : null,
                },
              })
            }
            popperModifiers={{
              preventOverflow: {
                enabled: true,
              },
            }}
          />
        </div>
        <span className="validation-error-message">
          {errorState && errorState?.expiry ? errorState?.expiry : ""}
        </span>
      </div>

      <div
        className={`form-group position-relative error-l-75 mb-5 ${
          errorState?.expiry ? "hasValidationError" : ""
        }`}
      >
        <div
          className={`toggle-switch ${!multipleUse ? "off" : "on"}`}
          onClick={() => {}}
        >
          <label>
            <b>Multiple Use</b>{" "}
            <i
              className="infoButton"
              data-tip={DISPLAY_TEXTS.INVITEFORM_MULTIPLEUSE_TOOLTIP}
            >
              i
            </i>
          </label>
          <span
            onClick={() =>
              updateInputChange({
                target: { name: "multipleUse", value: multipleUse },
              })
            }
          ></span>
        </div>
        <span className="validation-error-message">
          {errorState && errorState?.expiry ? errorState?.expiry : ""}
        </span>
      </div>

      {multipleUse && !moment(expiry, "DD-MM-YYYY").isValid() && (
        <div className="warning-text">{DISPLAY_TEXTS.INVITEFORM_WARNING}</div>
      )}
      {multipleUse && (
        <div className="warning-text">
          {DISPLAY_TEXTS.INVITEFORM_MULTIUSE_WARNING}
        </div>
      )}
      <ReactTooltip place="right" />
    </>
  );
};

export default PublicRegisterInviteFormComponent;
